import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { contentApi } from '../services/content-api';
import { useContentStore } from '../stores/contentStore';
import { branchKeys } from './queryKeys';
import { contentComparisonKeys } from './useContentComparison';
import type { ContentCreateInput, ContentUpdateInput } from '@echo-portal/shared';

export const contentKeys = {
  all: ['contents'] as const,
  lists: () => [...contentKeys.all, 'list'] as const,
  list: (branchId: string, filters?: Record<string, string>) =>
    [...contentKeys.lists(), branchId, filters] as const,
  details: () => [...contentKeys.all, 'detail'] as const,
  detail: (id: string) => [...contentKeys.details(), id] as const,
  published: (filters?: Record<string, string>) =>
    [...contentKeys.all, 'published', filters] as const,
  publishedBySlug: (slug: string) =>
    [...contentKeys.all, 'published', 'slug', slug] as const,
  search: (q: string, filters?: Record<string, string>) =>
    [...contentKeys.all, 'search', q, filters] as const,
};

/**
 * Fetch a single content item with its current version.
 *
 * When `keepPrevious` is set, the query returns the previously loaded content
 * while a new id is loading — useful for the edit view, which otherwise flashes
 * into preview while the new item's query settles.
 */
export function useContent(id: string | undefined, options?: { keepPrevious?: boolean }) {
  const setCurrentContent = useContentStore((s) => s.setCurrentContent);

  return useQuery({
    queryKey: contentKeys.detail(id ?? ''),
    queryFn: async () => {
      const content = await contentApi.getById(id!);
      setCurrentContent(content);
      return content;
    },
    enabled: !!id,
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/**
 * Fetch content list for a branch
 */
export function useContentList(
  branchId: string | undefined,
  params?: { contentType?: string; section?: string; category?: string; page?: number; limit?: number }
) {
  const setBranchContents = useContentStore((s) => s.setBranchContents);

  return useQuery({
    queryKey: contentKeys.list(branchId ?? '', params as Record<string, string>),
    queryFn: async () => {
      const result = await contentApi.listByBranch({
        branchId: branchId!,
        ...params,
      });
      setBranchContents(result.items);
      return result;
    },
    enabled: !!branchId,
  });
}

/**
 * Create new content mutation
 */
export function useCreateContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ContentCreateInput) => contentApi.create(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      // Invalidate branch query to update canSubmitForReview permission
      queryClient.invalidateQueries({ queryKey: branchKeys.detail(data.branchId) });
      // Keep the changelog badge + popover in sync in real time.
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      useContentStore.getState().setCurrentContent(data);
    },
  });
}

/**
 * Update content mutation (creates new version)
 */
export function useUpdateContent(contentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ContentUpdateInput) => contentApi.update(contentId, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      // Invalidate branch query to update canSubmitForReview permission
      queryClient.invalidateQueries({ queryKey: branchKeys.detail(data.branchId) });
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      useContentStore.getState().setCurrentContent(data);
    },
  });
}

/**
 * Delete content mutation (soft delete via archive)
 */
export function useDeleteContent(branchId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contentId: string) => contentApi.delete(contentId),
    onSuccess: (_data, contentId) => {
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      // Invalidate branch query to update canSubmitForReview permission
      if (branchId) {
        queryClient.invalidateQueries({ queryKey: branchKeys.detail(branchId) });
      }
      queryClient.invalidateQueries({ queryKey: contentComparisonKeys.all });
      useContentStore.getState().setCurrentContent(null);
    },
  });
}
